import React, { createContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLoaderData, useParams } from 'react-router-dom';
import { useIsLoggedIn } from './hooks/useIsLoggedIn';
import {
    ShopOGInfoProvider,
} from './providers/ShopOGInfoContext';
import { AppContextProvider, useAppContext } from './providers/AppProvider';
import { useLogout, useMemberLogin, useShopInfo } from './queries/shop';
import { RootHead } from './SEO/RootHead';
import { setIsAppAction } from './store/actions/MemberAction';
import { RNCenterLoading } from './components/RNCenterLoading';

export const ShopOGInfoContext = createContext({});

const Wrapper = () => {
    const { lang, shopId } = useAppContext();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();

    const { mutate: memberLoginSliently } = useMemberLogin(false);
    const { mutate: logoutSliently } = useLogout(false);
    const { mutate: logout } = useLogout();

    const isApp = useSelector(state => state.member.isApp);
    const isLoggedIn = useIsLoggedIn();

    const { isLoading, isError, error } = useShopInfo();
    const errorCode = error?.response?.status;

    useEffect(() => {
        if (window.ReactNativeWebView) {
            window.appLogin = (input) => {
                const appData = JSON.parse(input);
                
                const { 
                    memberid: appMemberId,
                    sid: appShopId,
                    sn: appShopName,
                } = appData;
    
                if (appShopId && appShopName && !isApp) {
                    dispatch(setIsAppAction(true));
                }
    
                if (appMemberId) {
                    memberLoginSliently({
                        lang,
                        shopId,
                        memberId: appMemberId,
                    });
                }
    
                if (!appMemberId) {
                    logoutSliently({
                        lang,
                        shopId,
                    });
                }
    
                return true;
            };

            window.ReactNativeWebView.postMessage(JSON.stringify({
                state: 'ready',
            }));
        }
    }, []);

    useEffect(() => {
        if (isApp) {
            document.addEventListener('contextmenu', (e) => {
                e.preventDefault();
            });
        }
    }, [isApp]);

    useEffect(() => {
        i18n.changeLanguage(lang);
    }, [lang]);

    useEffect(() => {
        if (errorCode === 403) {
            logout({
                lang,
                shopId,
            });
        }
    }, [errorCode, lang, shopId]);

    if (isLoading || isError) {
        let messages = '';

        if (error?.response?.data?.errorMsgs) {
            messages = error.response.data.errorMsgs
                .map(error => error.message)
                .concat('\n');
        }

        return <RNCenterLoading error={messages} />;
    }

    return <Outlet />;
};

export default () => {
    const { data } = useLoaderData();
    const { shopId } = data;
    const { lang, shopShortName } = useParams();
    const shopDomain = window.location.host;
    const isUsingShopDomain = shopShortName ? false : true;

    const AppContext = {
        shopShortName,
        shopId,
        shopDomain,
        lang,
        isUsingShopDomain,
    };

    return (
        <AppContextProvider value={AppContext}>
            <ShopOGInfoProvider>
                <RootHead />
                <Wrapper />
            </ShopOGInfoProvider>
        </AppContextProvider>
    );
};
